import { plural, t } from "@lingui/core/macro";
import React, { useCallback } from "react";
import RefinedListDropdown, {
  DynamicTitleFunction
} from "../RefinementListFilter/RefinedListDropdown";
import RefinedList from "../RefinementListFilter/RefinedList";
import { FycPromosContentFragment } from "types/graphql-types";
import { RefinementListItem } from "instantsearch.js/es/connectors/refinement-list/connectRefinementList";
import { cruisesLabel } from "src/utils/cruise";
import {
  descriptionForItem,
  excludePromosByConfigurationCodes,
  promosWithHideInFYCFlag,
  nameForItem
} from "components/FindYourCruise/Components/Filters/promo-filters";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";

type Props = {
  promosContent: FycPromosContentFragment[];
  excludeConfigurationCodes?: number[];
};

const buildDynamicTitle: DynamicTitleFunction = (count) =>
  count > 0
    ? plural(count, {
        one: "# offer selected",
        other: "# offers selected"
      })
    : t`Select the offer`;

const transformPromotions =
  (
    promosContent: FycPromosContentFragment[],
    excludeConfigurationCodes: number[]
  ) =>
  (items: RefinementListItem[]) => {
    const withNames = nameForItem(promosContent);
    const hiddenPromos = promosWithHideInFYCFlag(promosContent, items);
    const excluded = Array.from(
      new Set([...excludeConfigurationCodes, ...hiddenPromos])
    );
    const filterByConfigurationCodes =
      excludePromosByConfigurationCodes(excluded);

    return filterByConfigurationCodes(withNames(items));
  };

export const PromoFilterDropDown: React.FC<Props> = ({
  promosContent,
  excludeConfigurationCodes = []
}) => {
  const geolocation = useGeolocation();
  const countryBasedPromos = `countries.${geolocation?.countryCode}.pc`;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const transformItems = useCallback(
    transformPromotions(promosContent, excludeConfigurationCodes),
    [promosContent]
  );

  return (
    <RefinedListDropdown
      label={t`Offers`}
      dynamicTitle={buildDynamicTitle}
      dataContentPiece="promos_filter_interaction"
      attribute={countryBasedPromos}
      operator="and"
      transformItems={transformItems}
      descriptionForItem={descriptionForItem(promosContent)}
      limit={100}
      counterLabel={cruisesLabel}
    />
  );
};

export const PromoFilterList: React.FC<Props> = ({
  promosContent,
  excludeConfigurationCodes = []
}) => {
  const geolocation = useGeolocation();
  const countryBasedPromos = `countries.${geolocation?.countryCode}.pc`;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const transformItems = useCallback(
    transformPromotions(promosContent, excludeConfigurationCodes),
    [promosContent]
  );

  return (
    <RefinedList
      title={buildDynamicTitle}
      dataContentName="promos_filter_interaction"
      attribute={countryBasedPromos}
      operator="and"
      transformItems={transformItems}
      descriptionForItem={descriptionForItem(promosContent)}
      limit={100}
    />
  );
};
